import { ApolloLink } from '@apollo/client'
import { Observable } from 'rxjs'
import { print, type DocumentNode } from 'graphql'
import * as AbsintheSocket from '@absinthe/socket'
import type {
  AbsintheSocket as Socket,
  GqlRequest,
  Notifier,
  Observer
} from '@absinthe/socket'

/**
 * Replaces `@absinthe/socket-apollo-link`, which is built on apollo-link v1 and
 * bundles its own `graphql` copy. Same contract: a terminating ApolloLink that
 * pushes operations over the Phoenix socket.
 */
export const createAbsintheSocketLink = (absintheSocket: Socket) =>
  new ApolloLink(
    (operation) =>
      new Observable<ApolloLink.Result>((subscriber) => {
        const request: GqlRequest<Record<string, unknown>> = {
          operation: print(operation.query),
          variables: operation.variables
        }

        const notifier = AbsintheSocket.send(absintheSocket, request)

        const socketObserver: Observer<Record<string, unknown>> = {
          onAbort: (error) => subscriber.error(error),
          onResult: (result) => {
            subscriber.next(result as ApolloLink.Result)

            if (notifier.operationType !== 'subscription') {
              subscriber.complete()
            }
          }
          // `onError` is deliberately unwired, matching the link this replaces.
          // Phoenix reconnects and @absinthe/socket re-pushes its notifiers, so
          // erroring the observable here would permanently kill a subscription
          // that is about to recover on its own.
        }

        AbsintheSocket.observe(absintheSocket, notifier, socketObserver)

        return () => {
          // @absinthe/socket updates notifiers immutably, so the object captured
          // above may already be stale; `unobserve` throws on a detached
          // observer. Re-find by request identity, as the original link did.
          const current = absintheSocket.notifiers.find(
            (candidate: Notifier<Record<string, unknown>>) =>
              candidate.request === request
          )

          if (current) {
            AbsintheSocket.unobserveOrCancel(
              absintheSocket,
              current,
              socketObserver
            )
          }
        }
      })
  )

export const hasSubscription = (document: DocumentNode) =>
  document.definitions.some(
    (definition) =>
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
  )
